import type { AxiosError } from 'axios'
import moment from 'moment'
import { useMemo } from 'react'

import { useQuery, type UseQueryOptions } from '@tanstack/react-query'

import APIServices from '../../api/APIServices'

/**
 * Configuraciones generales públicas (`GET /public/configs`).
 *
 * OJO: no son las del negocio. Las del negocio viajan en `business.configurationsKey`
 * y salen de otra tabla; estas son globales de la región y el backend solo devuelve
 * las marcadas como públicas para el origen que las pide.
 */
export interface PublicConfig {
  key: string
  value: string
}

const PUBLIC_CONFIGS_QUERY_KEY = 'public-configs'

/**
 * Fecha a partir de la cual el costo de los movimientos sigue el estándar nuevo.
 * Vacía o ausente significa que todavía no se fijó: en ese caso la tabla no pinta
 * ni aviso ni guiones de histórico.
 */
export const MOVEMENT_COST_VALID_FROM_KEY = 'movement_cost_valid_from'

const fetchPublicConfigs = async (): Promise<PublicConfig[]> => {
  const response = await APIServices.get('/public/configs')
  return response.data
}

export const usePublicConfigsQuery = (
  options?: Partial<UseQueryOptions<PublicConfig[], AxiosError>>
) => {
  const queryResult = useQuery<PublicConfig[], AxiosError>({
    queryKey: [PUBLIC_CONFIGS_QUERY_KEY],
    queryFn: fetchPublicConfigs,
    refetchOnWindowFocus: false,
    // Cambian con un despliegue, no con el uso: revalidarlas seguido no aporta nada.
    staleTime: 30 * 60 * 1000,
    ...options,
  })

  return {
    publicConfigs: queryResult.data,
    isLoadingPublicConfigs: queryResult.isLoading,
    isErrorPublicConfigs: queryResult.isError,
  }
}

export interface MovementCostStandard {
  /** `null` mientras no haya una fecha configurada y entendible. */
  validFrom: Date | null
  /** Para el aviso: la fecha en formato local, o cadena vacía si no hay. */
  validFromLabel: string
  /**
   * Un movimiento anterior a la fecha viene de antes del estándar: su costo se
   * calculó con criterios distintos, así que se muestra en guion en vez de dar por
   * bueno un número que no es comparable con el resto.
   */
  isBeforeStandard: (_createdAt?: string | null) => boolean
}

/**
 * Lee la fecha de vigencia del estándar de costos y deja lista la comparación.
 *
 * Degradación limpia a propósito: si la clave no existe, viene vacía o trae algo que
 * no es una fecha, se comporta igual que si no estuviera configurada — sin aviso y sin
 * guiones. Nunca rompe la tabla por una config mal puesta.
 */
export const useMovementCostStandard = (): MovementCostStandard => {
  const { publicConfigs } = usePublicConfigsQuery()

  return useMemo(() => {
    const rawValue = publicConfigs?.find(
      (config) => config.key === MOVEMENT_COST_VALID_FROM_KEY
    )?.value

    const parsed = rawValue ? moment(rawValue) : null
    const validFrom = parsed?.isValid() ? parsed.startOf('day') : null

    return {
      validFrom: validFrom ? validFrom.toDate() : null,
      validFromLabel: validFrom ? validFrom.format('DD/MM/YYYY') : '',
      isBeforeStandard: (createdAt?: string | null) => {
        if (!validFrom || !createdAt) return false
        const movementDate = moment(createdAt)
        return movementDate.isValid() && movementDate.isBefore(validFrom)
      },
    }
  }, [publicConfigs])
}

export const getPublicConfigsQueryKey = () => [PUBLIC_CONFIGS_QUERY_KEY]
